import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getCollection } from "@/lib/api";
import { Seo } from "@/components/Seo";
import NotFound from "@/pages/NotFound";
import { ProductCard } from "@/components/ProductCard";
import { Reveal, MaskedLines } from "@/components/motion";
import { FALLBACK_IMG } from "@/lib/images";
import { ChevronRight } from "lucide-react";

export default function Collection() {
  const { slug } = useParams();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setCollection(null);
    setLoading(true);
    setNotFound(false);
    getCollection(slug)
      .then(setCollection)
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div data-testid="collection-loading" className="container-shule py-24 text-center text-shule-soft font-serif text-2xl">Yükleniyor…</div>;
  }
  if (notFound || !collection) return <NotFound />;

  const collectionUrl = `https://www.shuletaki.com.tr/koleksiyon/${collection.slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: collection.name,
      description: collection.story,
      url: collectionUrl,
      primaryImageOfPage: collection.hero_image,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://www.shuletaki.com.tr/" },
        { "@type": "ListItem", position: 2, name: collection.name, item: collectionUrl },
      ],
    },
  ];

  return (
    <>
      <Seo title={collection.name} description={collection.story} path={`/koleksiyon/${collection.slug}`} image={collection.hero_image} jsonLd={jsonLd} />
      <section className="relative overflow-hidden bg-shule-cream/60 border-b border-shule-line">
        <div className="container-shule py-14 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <nav className="flex items-center gap-1.5 text-xs text-shule-soft mb-5" aria-label="Sayfa yolu">
              <Link to="/" className="hover:text-shule-goldhover min-h-[24px] inline-flex items-center">Ana Sayfa</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-shule-mocha">Koleksiyon</span>
            </nav>
            <p className="eyebrow">{collection.chapter}</p>
            <h1 data-testid="collection-title" className="mt-3 font-serif text-5xl lg:text-6xl font-light text-shule-coffee">
              <MaskedLines lines={[collection.name]} />
            </h1>
            <p className="mt-5 text-shule-mocha text-lg leading-relaxed max-w-md">{collection.story}</p>
          </div>
          <Reveal className="arch-card max-w-md w-full ml-auto">
            <img src={collection.hero_image} alt={collection.name} onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
              className="w-full aspect-[4/3] object-cover" />
          </Reveal>
        </div>
      </section>

      <div className="container-shule py-12 lg:py-16">
        <Reveal className="mb-8">
          <p className="eyebrow">Setin Parçaları</p>
          <h2 className="mt-2 font-serif text-3xl text-shule-coffee">{collection.products?.length || 0} parça, tek hikâye</h2>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {collection.products?.map((p, i) => <ProductCard key={p.slug} product={p} index={i} arch={i % 2 === 0} />)}
        </div>
      </div>
    </>
  );
}
